import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DailyRewardCard, DailyRewardModal, GameGrid } from '@puzzlemania/lobby';
import type { DayReward } from '@puzzlemania/lobby';
import { DAILY_REWARD_DEFAULTS, DailyRewardService, liveopsStore } from '@puzzlemania/liveops';
import { SystemClock } from '@puzzlemania/core';
import { usePlatformServices, type ComposedServices } from '../composition/PlatformServicesContext';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const MODAL_SCHEDULE: DayReward[] = [1, 2, 3, 4, 5, 6, 7].map((day) => ({
  day,
  coins: DAILY_REWARD_DEFAULTS[day]?.coins ?? 100,
  gems: DAILY_REWARD_DEFAULTS[day]?.gems ?? 0,
}));

// Mirrors GameGrid's own PLACEHOLDER_GAMES catalog (packages/lobby) — kept here rather
// than imported from either game's package, matching that same file's existing pattern
// of the lobby/composition layer knowing game ids without depending on game internals.
// progressKey matches each game's LocalSaveModule key exactly (games/*/src/constants/
// save-keys.ts), which LocalSaveModule prefixes with `pm_save_` in localStorage —
// see packages/plugins/src/game-context-builder.ts.
const CONTINUABLE_GAMES = [
  { gameId: 'blockjam', nameKey: 'game.blockjam.name', progressKey: 'blockjam_progress' },
  { gameId: 'watersort', nameKey: 'game.watersort.name', progressKey: 'watersort_progress' },
] as const;

function getSavedGameIds(): string[] {
  if (typeof localStorage === 'undefined') return [];
  return CONTINUABLE_GAMES.filter((g) => {
    try {
      return localStorage.getItem(`pm_save_${g.progressKey}`) !== null;
    } catch {
      return false;
    }
  }).map((g) => g.gameId);
}

function formatCountdown(ts: number): string {
  const diff = Math.max(0, ts - Date.now());
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
}

function makeAppSvc(platform: ComposedServices): DailyRewardService {
  return new DailyRewardService(
    new SystemClock(),
    platform.economy,
    platform.remoteConfig,
    platform.dailyRewardSaveModule,
  );
}

export function HomeScreen({ svc: injectedSvc }: { svc?: DailyRewardService } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const platform = usePlatformServices();
  const svc = useRef(injectedSvc ?? makeAppSvc(platform)).current;

  useDocumentMeta(t('meta.home.title'), t('meta.home.description'));

  const canClaim = liveopsStore((s) => s.canClaim);
  const dayCounter = liveopsStore((s) => s.dayCounter);
  const streakCount = liveopsStore((s) => s.streakCount);
  const nextClaimTime = liveopsStore((s) => s.nextClaimTime);

  const [modalOpen, setModalOpen] = useState(false);
  const [showFlyout, setShowFlyout] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Re-read on every mount (not just once app-wide) — HomeScreen unmounts when
  // navigating into a game and remounts on the way back, so this naturally picks up
  // progress saved during the session that just ended.
  const [savedGameIds] = useState<string[]>(() => getSavedGameIds());
  const saveDataMap = Object.fromEntries(savedGameIds.map((id) => [id, true]));

  useEffect(() => {
    liveopsStore.setState({
      canClaim: svc.canClaim(),
      dayCounter: svc.getCurrentDay(),
      streakCount: svc.getStreakCount(),
      nextClaimTime: svc.getNextClaimTime(),
      lastClaimResult: null,
    });
  }, [svc]);

  function handleClaim() {
    // Captured before claim() advances the internal day counter — this is the day the
    // reward being granted right now was actually FOR, matching the already-registered
    // daily_reward_claimed schema's `streak_day` property (Analytics_Master_Schema.md).
    const claimedDay = svc.getCurrentDay();
    const result = svc.claim();
    if (result.ok) {
      // Real gap closed here (found during Module 22 planning): claiming previously
      // logged no analytics event at all. Fired once per currency actually granted,
      // matching the schema's singular-currency-per-event shape (coins are always
      // granted; gems only on some days) — not a new event, this one was already
      // registered but never implemented.
      platform.providers.analytics.logEvent('daily_reward_claimed', {
        reward_amount: result.value.coins,
        currency_type: 'coins',
        streak_day: claimedDay,
      });
      if (result.value.gems > 0) {
        platform.providers.analytics.logEvent('daily_reward_claimed', {
          reward_amount: result.value.gems,
          currency_type: 'gems',
          streak_day: claimedDay,
        });
      }
      liveopsStore.setState({
        canClaim: false,
        dayCounter: svc.getCurrentDay(),
        streakCount: svc.getStreakCount(),
        nextClaimTime: svc.getNextClaimTime(),
        lastClaimResult: result.value,
      });
      setShowFlyout(true);
      setModalOpen(false);
    } else {
      setToast(t('dailyReward.alreadyClaimed'));
      setModalOpen(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  return (
    <div className="screen screen--home">
      {savedGameIds.length > 0 && (
        <div className="continue-playing">
          <h2 className="home-screen__section-title">{t('game.continueSection.title')}</h2>
          <div
            className="continue-playing__list"
            role="list"
            aria-label={t('game.continueSection.title')}
          >
            {savedGameIds.map((gameId) => {
              const game = CONTINUABLE_GAMES.find((g) => g.gameId === gameId);
              if (!game) return null;
              return (
                <div key={gameId} role="listitem">
                  <button
                    className="continue-playing__item"
                    onClick={() => navigate(`/game/${gameId}`)}
                    data-testid={`continue-playing-${gameId}`}
                  >
                    {t(game.nameKey)}
                    <span className="continue-playing__item-cta">{t('game.badge.continue')}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <DailyRewardCard
        isClaimable={canClaim}
        streakCount={streakCount}
        todayRewardCoins={DAILY_REWARD_DEFAULTS[dayCounter]?.coins ?? 100}
        onTap={() => {
          if (canClaim) setModalOpen(true);
        }}
        {...(!canClaim ? { nextClaimTime: formatCountdown(nextClaimTime) } : {})}
      />
      <h2 className="home-screen__section-title">{t('game.section.title')}</h2>
      <GameGrid rc={platform.remoteConfig} saveDataMap={saveDataMap} />

      {modalOpen && (
        <DailyRewardModal
          dayCounter={dayCounter}
          streakCount={streakCount}
          schedule={MODAL_SCHEDULE}
          onClaim={handleClaim}
          onDismiss={() => setModalOpen(false)}
        />
      )}

      {showFlyout && (
        <div
          className="coin-flyout"
          aria-hidden="true"
          data-testid="coin-flyout"
          onAnimationEnd={() => setShowFlyout(false)}
        >
          🪙
        </div>
      )}

      {toast && (
        <div className="simple-toast" role="status" data-testid="toast">
          {toast}
        </div>
      )}
    </div>
  );
}
